<template>
  <!-- Table Container Card -->
  <b-card no-body>

    <div class="m-2">

      <!-- Table Top -->
      <b-row>
        <!-- Per Page -->
        <b-col
          cols="12"
          md="6"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
        >
          <TableEntries
            v-model="perPage"
            :options="perPageOptions"
          />
          <b-dropdown
            v-if="getUserPermission('DISBURSED_LOAN_REPORT')"
            id="dropdown-1"
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            :text="$t('Export')"
            variant="primary"
            :disabled="isDisabled"
          >
            <b-dropdown-item @click="csvExport(csvData)">
              {{ $t('CSV') }}
            </b-dropdown-item>
            <b-dropdown-item @click="excelExport(csvData)">
              {{ $t('Excel') }}
            </b-dropdown-item>
          </b-dropdown>
        </b-col>
        <!-- Export -->
        <b-col
          cols="12"
          md="6"
        >
          <div class="d-flex align-items-center justify-content-lg-end">
            <Button
              :label="$t('Filter')"
              size="md"
              icon="FilterIcon"
              show-icon
              class="mr-1"
              @click="showModal('modal-filter')"
            />
            <Button
              :label="$t('Reset Filter')"
              size="md"
              icon="RotateCwIcon"
              show-icon
              @click="resetFilter"
            />
          </div>
        </b-col>
      </b-row>
    </div>

    <b-table
      ref="DisburseLoanReportRef"
      :items="reportData"
      :busy="isBusy"
      :per-page="pageSize"
      :current-page="currentPage"
      responsive
      :fields="translatedTableHeaders"
      primary-key=""
      show-empty
      :empty-text="$t('No records available')"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      class="position-relative"
    >

      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner
            class="align-middle"
            variant="primary"
          />
        </div>
      </template>
      <template #cell(SN)="data">
        {{ getOverallIndex(data.index) }}
      </template>

      <template #cell(linkedAccountBalance)="data">
        <span class="text-nowrap">
          {{ toBalance(data.value) }}
        </span>
      </template>

      <template #cell(disbursedAmount)="data">
        <span class="text-nowrap">
          {{ toBalance(data.value) }}
        </span>
      </template>

      <template #cell(refinancedAmount)="data">
        <span class="text-nowrap">
          {{ toBalance(data.value) }}
        </span>
      </template>
      <template #cell(startDate)="data">
        <span class="text-nowrap">
          {{ data.value ? formatDate(data.value) : '' }}
        </span>
      </template>
      <template #cell(endDate)="data">
        <span class="text-nowrap">
          {{ data.value ? formatDate(data.value) : '' }}
        </span>
      </template>
      <template #cell(disbursementDate)="data">
        <span class="text-nowrap">
          {{ data.value ? formatDate(data.value) : '' }}
        </span>
      </template>
      <template #cell(accountStatus)="data">
        <b-badge
          pill
          :variant="getStatusColor(data.value)"
        >
          {{ data.value }}
        </b-badge>
      </template>
    </b-table>

    <TablePagination
      v-model="currentPage"
      :from="from"
      :to="to"
      :total-rows="totalEntries"
      :per-page="perPage"
    />

    <!-- modal -->
    <b-modal
      id="modal-filter"
      ref="my-modal"
      :ok-title="$t('Apply')"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      centered
      size="lg"
      no-close-on-backdrop
      :title="$t('Filters')"
      modal-footer
      @ok="handleSubmit"
      @cancel="resetFilters"
    >
      <validation-observer ref="DisbursedLoanRules">
        <b-form>
          <b-row class="match-height">
            <!-- Account Number -->
            <b-col md="6">
              <InputField
                id="account-number"
                v-model="filter.loanAccountNumber"
                :label="$t('Loan Account Number')"
                :placeholder="$t('Enter Loan Account Number')"
                type="text"
                for="account-number"
              />
            </b-col>
            <!-- Account Number -->
            <!-- Account Name -->
            <b-col md="6">
              <InputField
                id="account-name"
                v-model="filter.accountName"
                :label="$t('Account Name')"
                :placeholder="$t('Enter Loan Account Name')"
                type="text"
                for="account-name"
              />
            </b-col>
            <!-- Account Name -->
          </b-row>
          <b-row class="match-height">
            <!-- Disbursement Date (From) -->
            <b-col md="6">
              <DatePickerField
                id="start-date"
                v-model="filter.disbursementDateFrom"
                :min-date="new Date()"
                :label="$t('Disbursement Date (From)')"
                :config="datePickerConfig"
                :placeholder="$t('Select Disbursement Date (From)')"
              />
            </b-col>
            <!-- Disbursement Date (From) -->
            <!-- Disbursement Date (To) -->
            <b-col md="6">
              <DatePickerField
                id="end-date"
                v-model="filter.disbursementDateTo"
                :min-date="new Date()"
                :label="$t('Disbursement Date (To)')"
                :config="datePickerConfig"
                :placeholder="$t('Select Disbursement Date (To)')"
              />
            <!-- Disbursement Date (To) -->
            </b-col>
          </b-row>

          <b-row class="match-height">
            <!-- Financial Date (From) -->
            <b-col md="6">
              <DatePickerField
                id="financial-date-from"
                v-model="filter.financialDateFrom"
                :min-date="new Date()"
                :label="$t('Financial Date (From)')"
                :config="datePickerConfig"
                :placeholder="$t('Select Financial Date (From)')"
              />
            </b-col>
            <!-- Financial Date (From) -->
            <!-- Financial Date (To) -->
            <b-col md="6">
              <DatePickerField
                id="end-date"
                v-model="filter.financialDateTo"
                :min-date="new Date()"
                :label="$t('Financial Date (To)')"
                :config="datePickerConfig"
                :placeholder="$t('Select Financial Date (To)')"
              />
            <!-- Financial Date (To) -->
            </b-col></b-row>
          <b-row class="match-height">
            <!-- Product Name -->
            <b-col md="6">
              <SelectField
                id="product"
                v-model="filter.productCode"
                :reduce="option => option.value"
                :label="$t('Product Name')"
                :placeholder="$t('Select')"
                :options="productOptions"
                item="label"
              />
            </b-col>
            <!-- Product Name -->
            <!-- Branch -->
            <b-col md="6">
              <SelectField
                id="branch"
                v-model="filter.branchCode"
                :reduce="option => option.value"
                :label="$t('Branch')"
                :placeholder="$t('Select')"
                :options="branchOptions"
                item="label"
                @input="getBranchValue(filter.branchCode)"
              />
            </b-col>
            <!-- Branch -->
            <!-- IPPIS/Oracle ID -->
            <b-col md="6">
              <InputField
                id="ippis-oracle-id"
                v-model="filter.employeeID"
                :label="$t('IPPIS/Oracle ID')"
                :placeholder="$t('Enter IPPIS/Oracle ID')"
                type="text"
                for="ippis-oracle-id"
              />
            </b-col>
            <!--IPPIS/Oracle ID -->
            <!-- Branch -->
            <b-col md="6">
              <SelectField
                id="account-status"
                v-model="filter.accountStatus"
                :reduce="option => option.value"
                :label="$t('Account Status')"
                :placeholder="$t('Select')"
                :options="translatedAccountStatusOptions"
                item="label"
              />
            </b-col>
            <!-- Branch -->
            <b-col md="6">
              <SelectField
                id="account-officer"
                v-model="filter.accountOfficerStaffId"
                :label="$t('Account Officer')"
                :options="accountOfficerOptions"
                :reduce="option => option.value"
                item="label"
                :placeholder="$t('Select')"
              />
            </b-col>
          </b-row>

        </b-form>
      </validation-observer>

      <template
        v-if="isReadonly"
        #modal-footer="{ }"
      >
        <div />
      </template>
    </b-modal>
    <!-- modal -->
  </b-card>

</template>

<script>
import {
  BCard, BRow, BCol, BTable, BSpinner, BModal, BForm, BDropdown, BDropdownItem, BBadge,
} from 'bootstrap-vue'
import { ValidationObserver } from 'vee-validate'
import { required } from '@validations'
import { tableMixins } from '@core/mixins/table'
import { modalMixins } from '@core/mixins/modal'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { permissionMixin } from '@core/mixins/permission'
import { mapGetters, mapActions } from 'vuex'
import Ripple from 'vue-ripple-directive'
import { heightFade } from '@core/directives/animations'
import { toBalance, toFormatAmount, formatAmount } from '@/helpers/formatAmount'
import toNumber from '@/helpers/toNumber'
import getStatusColor from '../../../../helpers/getStatusColor'
import { toDateWithYear, toCustomDate, toDateWithYearFirst } from '@/helpers/formatDate'
import TablePagination from '@/views/components/table/TablePagination.vue'
import TableEntries from '@/views/components/table/TableEntries.vue'
import SelectField from '../../../forms/SelectField.vue'
import InputField from '../../../forms/InputField.vue'
import Button from '../../../components/button/Button.vue'
import DatePickerField from '../../../forms/DatePickerField.vue'
import { reportPermissions } from '../../../../helpers/permissions'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    Button,
    BSpinner,
    BTable,
    BBadge,
    TablePagination,
    InputField,
    SelectField,
    BModal,
    BForm,
    BDropdown,
    BDropdownItem,
    TableEntries,
    DatePickerField,
    ValidationObserver,
  },
  directives: {
    Ripple,
    'height-fade': heightFade,
  },
  mixins: [modalMixins, tableMixins, sweetAlert, permissionMixin],
  data() {
    const now = new Date()
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1)
    const todayDay = new Date(today)
    return {
      toBalance,
      getStatusColor,
      toFormatAmount,
      toDateWithYearFirst,
      firstDate: firstDay,
      todayDate: todayDay,
      today,
      currentDate: '',
      query: '',
      statusToggleLabel: '',
      isReadonly: false,
      buttonLabel: this.$t('Save'),
      isEditable: false,
      isDisabled: true,
      isBusy: false,
      required,
      modalTitle: this.$t('Disbursed Loan Report'),
      sortBy: 'accountName',
      sortDesc: false,
      tableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'accountNumber', sortable: true, label: 'Loan Account Number' },
        { key: 'accountName', sortable: true, label: 'Account Name' },
        { key: 'linkedAccount', sortable: true, label: 'Linked Account No.' },
        { key: 'linkedAccountBalance', sortable: true, label: 'Linked Account Balance' },
        { key: 'employeeID', sortable: true, label: 'IPPIS/Oracle ID' },
        { key: 'accountOfficer', sortable: true, label: 'Account Officer' },
        { key: 'branch', sortable: true, label: 'Branch' },
        { key: 'product', sortable: true, label: 'Product' },
        { key: 'tenure', sortable: true, label: 'Tenure' },
        { key: 'disbursementDate', sortable: true, label: 'Disbursement Date' },
        { key: 'startDate', sortable: true, label: 'Start Date' },
        { key: 'endDate', sortable: true, label: 'End Date' },
        { key: 'disbursedAmount', sortable: true, label: 'Disbursed Amount' },
        { key: 'refinancedAmount', sortable: true, label: 'Refinanced Amount' },
        { key: 'interestRate', sortable: true, label: 'Interest Rate (PA)' },
        { key: 'phoneNumber', sortable: true, label: 'Cust. Phone No.' },
        { key: 'gender', sortable: true, label: 'Gender' },
        { key: 'guarantorName', sortable: true, label: 'Guarantor Name' },
        { key: 'guarantorPhoneNumber', sortable: true, label: 'Guarantor Phone Number' },
        { key: 'accountStatus', sortable: true, label: 'Status' },
      ],
      filter: {
        loanAccountNumber: '',
        accountName: '',
        disbursementDateFrom: null,
        disbursementDateTo: null,
        financialDateFrom: null,
        financialDateTo: null,
        branchCode: '',
        productCode: '',
        employeeID: '',
        accountStatus: '',
        accountOfficerStaffId: '',
      },
      datePickerConfig: {
        dateFormat: 'Y-m-d',
        maxDate: '',
        defaultDate: '',
      },
      productOptions: [],
      allSearchedData: [],
      branchRefCode: '',
      reportData: [],
      selected: '',
      accountStatusOptions: [
        { label: 'Active', value: 'Active' },
        { label: 'Closed', value: 'Closed' },
      ],
      accountOfficerOptions: [],
    }
  },
  computed: {
    ...mapGetters({
      userDetails: 'auth/getCurrentUser',
      branchOptions: 'operations/branchOptions',
    }),
    csvData() {
      return this.exportItems.map(item => ({
        ...item,
      }))
    },
    exportItems() {
      return this.allSearchedData.map(item => ({
        'Account Name': item.accountName,
        'Loan Account Number': item.accountNumber,
        'Linked Account Number': item.linkedAccount,
        'Linked Account Balance': item.linkedAccountBalance,
        'IPPIS/Oracle ID': item.employeeID,
        'Account Officer': item.accountOfficer,
        Branch: item.branch,
        Product: item.product,
        Tenure: item.tenure,
        'Disbursement Date': this.formatDate(item.disbursementDate),
        'Start Date': this.formatDate(item.startDate),
        'End Date': this.formatDate(item.endDate),
        'Disburse Amount': item.disbursedAmount,
        'Refinance Amount': item.refinancedAmount,
        'Interest Rate': item.interestRate,
        'Customer Phone Number': item.phoneNumber,
        Gender: item.gender,
        'Guarantor Name': item.guarantorName,
        'Guarantor Phone': item.guarantorPhoneNumber,
        Status: item.accountStatus,
      }))
    },
    translatedTableHeaders() {
      return this.tableHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
    translatedAccountStatusOptions() {
      return this.accountStatusOptions.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.value }))
    },
  },
  watch: {
    currentPage(newValue) {
      this.currentPage = newValue
      this.fetchDisbursedLoanReport()
    },
    perPage(newValue) {
      this.perPage = newValue
      this.fetchDisbursedLoanReport()
    },
  },
  mounted() {
    this.fetchProduct()
    this.fetchDisbursedLoanReport()
    this.fetchAccountOfficers()
  },
  methods: {
    ...mapActions({
      getDisbursedLoanReport: 'reports/getDisbursedLoansReports',
      getBranch: 'operations/getBranchList',
      getBranchByCode: 'operations/getBranchByCode',
      getProduct: 'account/getProducts',
      getAccountOfficers: 'account/getAccountOfficerList',
      downloadDisbursedLoanReport: 'reports/downloadDisbursedLoanReport',
    }),
    async fetchAccountOfficers() {
      try {
        const { data, status } = await this.getAccountOfficers({
          search: '',
          page: 0,
          limit: 0,
          permission: reportPermissions.DISBURSED_LOAN_REPORT,
        })

        if (status === 200 && data.data) {
          this.accountOfficerOptions = data.data.filter(item => item.status === 'Active').map(item => ({ label: item.name, value: item.staffID }))
        }
      } catch (error) {
        this.alertError({ text: error })
      }
    },
    resetFilters() {
      this.filter = { ...null }
    },
    resetFilter() {
      this.filter = { ...null }
      this.filter.disbursementDateFrom = null
      this.filter.disbursementDateTo = null
      this.fetchDisbursedLoanReport()
    },
    async fetchProduct() {
      try {
        const response = await this.getProduct({
          search: '',
          page: 0,
          limit: 0,
          permission: reportPermissions.DISBURSED_LOAN_REPORT,
        })
        const { status, data } = response
        if (status === 200 && data.data) {
          this.productOptions = data.data.filter(dataItem => dataItem.status === 'Active').map(item => ({ label: item.name, value: item.code }))
        }
      } catch (error) {
        this.alertError({ text: error })
      }
    },
    async getBranchValue(dataValue) {
      if (dataValue !== null) {
        try {
          const response = await this.getBranchByCode(dataValue)
          const { status, data } = response
          if (status === 200 && data) {
            this.branchRefCode = data
          }
        } catch (error) {
          this.alertError({ text: error })
        } finally {
          this.isBusy = false
        }
      }
    },
    handleSubmit(bvModalEvt) {
      try {
        bvModalEvt.preventDefault()
        this.$refs.DisbursedLoanRules.validate().then(async success => {
          if (success) {
            this.fetchDisbursedLoanReport()
          }
        })
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isLoading = false
        this.hideModal('modal-filter')
      }
    },
    async fetchDisbursedLoanReport() {
      try {
        this.isBusy = true
        if (this.filter.disbursementDateFrom === null && this.filter.disbursementDateTo === null) {
          this.currentDate = {
            startDate: this.toDateWithYearFirst(this.firstDate),
            endDate: this.toDateWithYearFirst(this.todayDate),
          }
        }
        this.hideModal('modal-filter')
        const response = await this.getDisbursedLoanReport({
          search: this.filter,
          page: this.currentPage,
          limit: this.perPage,
        })
        const { status, data } = response
        if (status === 200 && data.data) {
          this.reportData = data.data
          this.reportData = data.data.map(items => ({ ...items, linkedAccountBalance: Number(items.linkedAccountBalance) }))
          this.totalCount = data.recordCount
          this.isBusy = false
          this.isDisabled = false
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },
    async csvExport() {
      try {
        this.isBusy = true
        const response = await this.downloadDisbursedLoanReport({
          search: this.filter,
          fileExportFormat: 1,
          permission: reportPermissions.DISBURSED_LOAN_REPORT,
        })
        if (response.status === 200) {
          this.alertSuccessCustom({
            text: this.$t('Your download is in progress and might take a little longer. Go to your'),
            link: {
              text: this.$t('Download Manager'),
              route: { name: 'download-manager' },
            },
            afterLinkText: this.$t('to access the completed file.'),
          })
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },
    async excelExport() {
      try {
        this.isBusy = true
        const response = await this.downloadDisbursedLoanReport({
          search: this.filter,
          fileExportFormat: 0,
          permission: reportPermissions.DISBURSED_LOAN_REPORT,
        })
        if (response.status === 200) {
          this.alertSuccessCustom({
            text: this.$t('Your download is in progress and might take a little longer. Go to your'),
            link: {
              text: this.$t('Download Manager'),
              route: { name: 'download-manager' },
            },
            afterLinkText: this.$t('to access the completed file.'),
          })
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },
    toAmount: formatAmount,
    formatNumber: toNumber,
    formatCustomDate: toCustomDate,
    formatDate: toDateWithYear,
  },
}
</script>

<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}

.invoice-filter-select {
  min-width: 190px;

  ::v-deep .vs__selected-options {
    flex-wrap: nowrap;
  }

  ::v-deep .vs__selected {
    width: 100px;
  }
}
</style>

<style lang="scss">
    @import '@core/scss/vue/libs/vue-select.scss';
</style>
